import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import baseAPI from '../../api/ApiConnector_v2';
import { getEnv } from '../../config/Env';
import type { Area, Document, DocumentVariant, Tag, VariantPackage } from '../../models/documents.models';
import type {
    Category,
    Reimbursement,
    ReimbursementProduct,
    ReimbursementProductField,
} from '../../models/reimbursement.models';
import type {
    AreaBaseBlock,
    EntityReference,
    Translation,
    TranslationContainer,
    TranslationKey,
    TranslationLanguage,
} from '../../models/translation.models';
import HelperFunctions from '../../pages/global/HelperFunctions';
import { translationContainerTypes } from '../../pages/translation/config/Constants';
import { baseQueryWithReauth, isJsonContentType, prepareHeaders, providesList } from '../api';

const translationApiUrl = getEnv('TRANSLATION_API_BASE_URL');
const appApiUrl = getEnv('APP_API_URL');
const reimbursementApiUrl = getEnv('REIMBURSEMENT_API_BASE_URL');

const translationApiQuery = fetchBaseQuery({
    baseUrl: translationApiUrl,
    prepareHeaders,
    isJsonContentType,
    responseHandler: 'content-type',
});

const appApiQuery = fetchBaseQuery({
    baseUrl: appApiUrl,
    prepareHeaders,
});

const reimbursementApiQuery = fetchBaseQuery({
    baseUrl: reimbursementApiUrl,
    prepareHeaders,
    isJsonContentType,
    responseHandler: 'content-type',
});

interface ApiResponse<T> {
    'hydra:member': T[];
}

export const translationApi = createApi({
    reducerPath: 'translations',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, translationApiQuery),
    tagTypes: ['TranslationContainer', 'TranslationKey', 'Translation', 'EntityReference', 'TranslationLanguage'],
    endpoints: (builder) => ({
        getTranslationContainers: builder.query<TranslationContainer[], void>({
            query: () => '/api/translation_containers',
            transformResponse(response: ApiResponse<TranslationContainer>) {
                return response['hydra:member'];
            },
            providesTags: (result) => providesList(result, 'TranslationContainer'),
        }),
        getTranslationLanguages: builder.query<TranslationLanguage[], string>({
            query: (id) => `/api/translation_containers/${id}/translation_languages`,
            transformResponse(response: ApiResponse<TranslationLanguage>) {
                return response['hydra:member'];
            },
            providesTags: (result) => providesList(result, 'TranslationLanguage'),
        }),
        getTranslationContainer: builder.query<TranslationContainer, string>({
            query: (id) => {
                if (id.startsWith('/api')) {
                    // id is uri
                    return id;
                }

                return `/api/translation_containers/${id}`;
            },
            providesTags: (result) => (result ? [{ type: 'TranslationContainer', id: result['@id'] }] : []),
        }),
        getEntityReference: builder.query<EntityReference, string>({
            query: (uri) => uri,
            providesTags: (result) => (result ? [{ type: 'EntityReference', id: result['@id'] }] : []),
        }),
        getTranslationContainerEntityReferences: builder.query<
            EntityReference[],
            { uri: string; entityType?: string; entityId?: string; parent?: string }
        >({
            query: ({ uri, ...params }) => {
                return {
                    url: `${uri}/entity_references`,
                    method: 'get',
                    params: params,
                };
            },
            transformResponse(response: ApiResponse<EntityReference>) {
                return response['hydra:member'];
            },
            providesTags: (result) => {
                const results = result as EntityReference[];
                const tags = providesList(results, 'EntityReference');

                results.forEach((ref) => {
                    if (ref.parent) {
                        tags.push({ type: 'EntityReference', id: ref.parent });
                    }
                });

                return tags;
            },
        }),
        getTranslationKeys: builder.query<TranslationKey[], { uri: string; entityType: string; property?: string }>({
            query: ({ uri, entityType, property }) => {
                return {
                    url: `${uri}/translation_keys`,
                    method: 'get',
                    params: { entityType: entityType, property: property },
                };
            },
            transformResponse(response: ApiResponse<TranslationKey>) {
                return response['hydra:member'];
            },
            providesTags: (result) => providesList(result, 'TranslationKey'),
        }),
        getTranslationKey: builder.query<TranslationKey, string>({
            query: (id) => {
                return id.startsWith('/api') ? id : `/api/translation_keys/${id}`;
            },
            providesTags: (result) => (result ? [{ type: 'TranslationKey', id: result['@id'] }] : []),
        }),
        getTranslationKeyParentEntityReferences: builder.query<EntityReference[], { uri: string }>({
            query: ({ uri }) => {
                return {
                    url: `${uri}/parent_entity_references`,
                    method: 'get',
                };
            },
            transformResponse(response: ApiResponse<EntityReference>) {
                return response['hydra:member'];
            },
            providesTags: (_result, _error, { uri }) => [{ type: 'TranslationKey', id: uri }],
        }),
        getTranslationLanguageExport: builder.query<string, { uri: string; params: any }>({
            query: ({ uri, params }) => ({
                url: `${uri}/export`,
                params: { ...params },
                headers: {
                    'content-type': 'application/xml',
                },
                responseHandler: (response: Response) => response.text(),
            }),
        }),
        getTranslationLanguageExportFilter: builder.query<string, { id: string; params: any; body: any }>({
            query: ({ id, params, body }) => ({
                url: `/api/translation_languages/${id}/export_filter`,
                method: 'POST',
                params: { ...params },
                body,
            }),
        }),
        updateTranslationKey: builder.mutation<TranslationKey, { uri: string; body: Partial<TranslationKey> }>({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { uri }) => [{ type: 'TranslationKey', id: uri }],
        }),
        updateEntityReference: builder.mutation<EntityReference, { uri: string; body: Partial<EntityReference> }>({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { uri }) => [{ type: 'EntityReference', id: uri }],
        }),
        getTranslations: builder.query<Translation[], string>({
            query: (uri) => {
                return {
                    url: `${uri}/translations`,
                    method: 'get',
                };
            },
            transformResponse: (response: ApiResponse<Translation>) => response['hydra:member'],
            providesTags: (result, _error, uri) => [
                ...providesList(result, 'Translation'),
                { type: 'TranslationKey', id: uri },
            ],
        }),
        addTranslation: builder.mutation<Translation, Partial<Translation>>({
            query: (body) => ({
                url: '/api/translations',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'TranslationKey', id: arg.translationKey }],
            async onQueryStarted({ translationKey }, { dispatch, queryFulfilled }) {
                try {
                    if (!translationKey) return;

                    await queryFulfilled;

                    const { data: translationKeyData } = await dispatch(
                        translationApi.endpoints.getTranslationKey.initiate(translationKey),
                    );

                    const entityRefUri =
                        translationKeyData?.entityReferences?.[0] || translationKeyData?.firstEntityReference?.['@id'];

                    if (entityRefUri) {
                        dispatch(translationApi.util.invalidateTags([{ type: 'EntityReference', id: entityRefUri }]));
                    }
                } catch {
                    // Silently handle error
                }
            },
        }),
        updateTranslation: builder.mutation<Translation, { uri: string; body: Partial<Translation> }>({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({ uri }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        translationApi.util.updateQueryData('getTranslations', data.translationKey, (draft) => {
                            const oldValue = draft.find((_item) => _item['@id'] === uri);

                            if (oldValue) {
                                Object.assign(oldValue, data);
                            }
                        }),
                    );

                    if (data?.translationKey) {
                        const tags = [{ type: 'TranslationKey', id: data?.translationKey }];

                        const { data: translationKeyData } = await dispatch(
                            translationApi.endpoints.getTranslationKey.initiate(data.translationKey),
                        );

                        if (translationKeyData) {
                            const entityRefUri =
                                translationKeyData.firstEntityReference?.['@id'] ||
                                translationKeyData.entityReferences?.[0];

                            if (entityRefUri) {
                                tags.push({ type: 'EntityReference', id: entityRefUri });
                            }

                            // For block translations, also invalidate the parent area's EntityReference
                            // so that the nav sidebar counters update correctly
                            if (translationKeyData.firstEntityReference?.parent) {
                                tags.push({
                                    type: 'EntityReference',
                                    id: translationKeyData.firstEntityReference.parent,
                                });
                            }
                        }

                        dispatch(translationApi.util.invalidateTags(tags));
                    }
                } catch {
                    console.log('error');
                }
            },
        }),
        addTranslationContainer: builder.mutation<TranslationContainer, Partial<TranslationContainer>>({
            query: (body) => ({
                url:
                    body.type === translationContainerTypes.DOCUMENT
                        ? '/api/document_containers'
                        : body.type === translationContainerTypes.REIMBURSEMENT
                          ? '/api/reimbursement_containers'
                          : '/api/translation_containers',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['TranslationContainer'],
        }),
        deleteTranslationContainer: builder.mutation<void, string>({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'TranslationContainer', id: 'LIST' }],
        }),
        addTranslationLanguage: builder.mutation<TranslationLanguage, Partial<TranslationLanguage>>({
            query: (body) => ({
                url: '/api/translation_languages',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) =>
                result
                    ? [
                          { type: 'TranslationLanguage', id: 'LIST' },
                          { type: 'TranslationContainer', id: result.translationContainer },
                      ]
                    : [],
        }),
        updateTranslationLanguage: builder.mutation<
            TranslationLanguage,
            { uri: string; body: Partial<TranslationLanguage> }
        >({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { uri }) => [
                { type: 'TranslationLanguage', id: uri },
                { type: 'TranslationContainer' },
            ],
        }),
        deleteTranslationLanguage: builder.mutation<void, string>({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'TranslationLanguage', id: 'LIST' }],
        }),
        syncTranslationLanguage: builder.mutation<TranslationLanguage, string>({
            query: (uri) => ({
                url: uri + '/sync',
                method: 'PUT',
                body: {},
                headers: {
                    'content-type': 'application/json',
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'TranslationLanguage', id: arg }],
        }),
        updateTranslationContainer: builder.mutation<
            TranslationContainer,
            { uri: string; body: Partial<TranslationContainer> }
        >({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { uri }) => [{ type: 'TranslationContainer', id: uri }],
        }),
        importXliffFile: builder.mutation<any, any>({
            query: ({ uri, body }) => ({
                url: `${uri}/import/preview`,
                method: 'POST',
                body,
                formData: true,
            }),
        }),
        processImportXliffContent: builder.mutation<any, any>({
            query: ({ uri, body }) => ({
                url: `${uri}/import/process`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, { uri }) => [
                { type: 'TranslationLanguage', id: uri },
                { type: 'EntityReference' },
                { type: 'Translation' },
                { type: 'TranslationKey' },
            ],
        }),
        getReimbursementProductKeys: builder.query<any, any>({
            query: ({ id, params }) => {
                return {
                    url: `/api/reimbursement_containers/${id}/reimbursement_products_keys`,
                    method: 'get',
                    params,
                };
            },
            providesTags: (result) => [...providesList(result['hydra:member'] ?? [], 'TranslationKey')],
        }),
        getCategoryVariantPackagesKeys: builder.query<any, any>({
            query: ({ uri, params }) => {
                return {
                    url: `${uri}/category_variant_packages_keys`,
                    method: 'get',
                    params,
                };
            },
            providesTags: (result) => [...providesList(result['hydra:member'] ?? [], 'TranslationKey')],
        }),
        addEntity: builder.mutation<{ uri: string; body: Partial<EntityReference> }, Partial<EntityReference>>({
            query: ({ uri, body }) => ({
                url: `${uri}/add_entity`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'EntityReference', id: 'LIST' }],
        }),
        addNewTranslation: builder.mutation<{ uri: string; body: Partial<Translation> }, Partial<Translation>>({
            query: ({ uri, body }) => ({
                url: `${uri}/add_translation`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result: Translation | undefined) => {
                if (!result) {
                    return [];
                }

                return [{ type: 'TranslationKey', id: result.translationKey }];
            },
        }),
        addNewTranslationKey: builder.mutation<{ uri: string; body: Partial<TranslationKey> }, Partial<TranslationKey>>(
            {
                query: ({ uri, body }) => ({
                    url: `${uri}/add_translation_key`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (result, error, arg) => {
                    const tags = [
                        { type: 'TranslationKey', id: 'LIST' },
                    ];

                    const { body } = arg;

                    if (body.parentUri) {
                        tags.push({ type: 'EntityReference', id: body.parentUri });
                    }

                    return tags;
                },
            },
        ),
        getAreaBaseBlocks: builder.query<AreaBaseBlock[], string>({
            query: (uri) => `${uri}/area_blocks`,
            transformResponse: (response: ApiResponse<AreaBaseBlock>) => response['hydra:member'],
            providesTags: (_result, _error, uri) => [{ type: 'EntityReference', id: uri }],
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetTranslationContainersQuery,
    useGetTranslationContainerQuery,
    useGetTranslationLanguagesQuery,
    useGetEntityReferenceQuery,
    useGetTranslationContainerEntityReferencesQuery,
    useGetTranslationKeysQuery,
    useGetTranslationKeyQuery,
    useGetTranslationKeyParentEntityReferencesQuery,
    useGetTranslationLanguageExportQuery,
    useUpdateTranslationLanguageMutation,
    useGetTranslationLanguageExportFilterQuery,
    useUpdateTranslationKeyMutation,
    useUpdateEntityReferenceMutation,
    useGetTranslationsQuery,
    useAddTranslationMutation,
    useUpdateTranslationMutation,
    useAddTranslationContainerMutation,
    useDeleteTranslationContainerMutation,
    useAddTranslationLanguageMutation,
    useDeleteTranslationLanguageMutation,
    useSyncTranslationLanguageMutation,
    useUpdateTranslationContainerMutation,
    useImportXliffFileMutation,
    useProcessImportXliffContentMutation,
    useGetReimbursementProductKeysQuery,
    useGetCategoryVariantPackagesKeysQuery,
    useAddEntityMutation,
    useAddNewTranslationMutation,
    useAddNewTranslationKeyMutation,
    useGetAreaBaseBlocksQuery,
} = translationApi;

export function exportFilterDownload(id: string, body: object) {
    return baseAPI.post(`${translationApiUrl}/api/translation_languages/${id}/export_filter_download`, body, {
        responseType: 'blob',
    });
}

export function useGetEntityReference(
    translationContainerUri: string,
    entityType: string,
    entityUri: string,
    skip = false,
): EntityReference | undefined {
    const { entityReference }: { entityReference: EntityReference | undefined } =
        useGetTranslationContainerEntityReferencesQuery(
            {
                uri: translationContainerUri,
                entityType,
            },
            {
                selectFromResult: ({ data }) => ({
                    entityReference:
                        data === undefined ? undefined : data.find((_item: EntityReference) => _item.uri === entityUri),
                }),
                skip,
            },
        );

    return entityReference;
}

export const translationDocumentApi = createApi({
    reducerPath: 'translationDocuments',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, appApiQuery),
    tagTypes: ['Document', 'DocumentVariant', 'Area', 'Tag'],
    endpoints: (builder) => ({
        getDocument: builder.query<Document, number>({
            query: (id) => `/v2/documents/${id}`,
            providesTags: (result) => (result ? [{ type: 'Document', id: result.id }] : []),
        }),
        getDocuments: builder.query<Document[], number>({
            query: (id) => `/organisations/${id}/documents`,
            providesTags: (result) => providesList(result, 'Document', 'id'),
        }),
        getDocumentVariants: builder.query<DocumentVariant, number>({
            query: (id) => `/documents/${id}/variants`,
            providesTags: (result) => (result ? [{ type: 'DocumentVariant', id: result.id }] : []),
        }),
        getDocumentTags: builder.query<Tag[], number>({
            query: (id) => `/documents/${id}/tags`,
            providesTags: (result) => providesList(result, 'Tag', 'id'),
            transformResponse: (response: Tag[]) =>
                response ? response.sort(HelperFunctions.dynamicSort('name')) : response,
        }),
        getTag: builder.query<Tag, { documentId: number; tagId: number }>({
            query: ({ documentId, tagId }) => `/documents/${documentId}/tags/${tagId}`,
            providesTags: (result) => (result ? [{ type: 'Tag', id: result.id }] : []),
        }),
        getArea: builder.query<{ area: Area }, { id: number; variantId: number }>({
            query: ({ id, variantId }) => `/areas/${id}/variants/${variantId}/blocks?processTags=false`,
            providesTags: (result) => (result ? [{ type: 'Area', id: result.area.id }] : []),
        }),
    }),
});

export const {
    useGetDocumentQuery,
    useGetDocumentsQuery,
    useGetDocumentVariantsQuery,
    useGetDocumentTagsQuery,
    useGetTagQuery,
    useGetAreaQuery,
} = translationDocumentApi;

export const translationReimbursementApi = createApi({
    reducerPath: 'translationReimbursements',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, reimbursementApiQuery),
    tagTypes: [
        'Category',
        'Reimbursement',
        'Product',
        'ReimbursementProduct',
        'VariantPackage',
        'ReimbursementProductField',
    ],
    endpoints: (builder) => ({
        getCategories: builder.query<Category[], void>({
            query: () => `/api/reimbursement-api/api/categories`,
            transformResponse: (response: ApiResponse<Category>) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'Category'),
            keepUnusedDataFor: 3600, // keep data in cache for 1 hour
        }),
        getReimbursements: builder.query<Reimbursement[], string>({
            query: (uri) => `${uri}/reimbursements`,
            transformResponse: (response: ApiResponse<Reimbursement>) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'Reimbursement'),
            keepUnusedDataFor: 3600, // keep data in cache for 1 hour
        }),
        getReimbursementProduct: builder.query<ReimbursementProduct, string>({
            query: (uri) => uri,
            providesTags: (result) => (result ? [{ type: 'ReimbursementProduct', id: result.id }] : []),
            keepUnusedDataFor: 3600, // keep data in cache for 1 hour
        }),
        getVariantPackages: builder.query<VariantPackage[], void>({
            query: () => '/api/reimbursement-api/api/variant_packages',
            transformResponse: (response: ApiResponse<VariantPackage>) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'VariantPackage'),
            keepUnusedDataFor: 3600, // keep data in cache for 1 hour
        }),
        getReimbursementProductField: builder.query<ReimbursementProductField, string>({
            query: (uri) => uri,
            providesTags: (result) => (result ? [{ type: 'ReimbursementProductField', id: result.slug }] : []),
            keepUnusedDataFor: 3600, // keep data in cache for 1 hour
        }),
    }),
});

export const {
    useGetCategoriesQuery,
    useGetReimbursementsQuery,
    useGetReimbursementProductQuery,
    useGetVariantPackagesQuery,
    useGetReimbursementProductFieldQuery,
} = translationReimbursementApi;
